import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Calendar } from 'lucide-react';
import { FaqSection } from '@/components/FaqSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

export const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Blog · Eat From Local — Home Cook Stories, Tips & Guides';
    axios.get(`${API}/blog`)
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }} data-testid="blog-list-page">
      {/* Header */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 pt-14 md:pt-20 pb-8">
        <p
          className="uppercase mb-4"
          style={{ fontFamily: 'Manrope', fontSize: '12px', fontWeight: 600, letterSpacing: '0.18em', color: '#D95A41' }}
        >
          The Eat From Local Journal
        </p>
        <h1
          style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
          className="text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4"
        >
          Stories, tips & guides from your neighborhood kitchen.
        </h1>
        <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }} className="text-base md:text-lg max-w-2xl">
          Practical advice for home cooks starting out, food-safety essentials, and the human stories behind the meals in your suburb.
        </p>
      </section>

      {/* Posts */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 pb-16 md:pb-24">
        {loading ? (
          <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }} className="py-12 text-center">Loading posts…</p>
        ) : posts.length === 0 ? (
          <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }} className="py-12 text-center">No blog posts yet — check back soon.</p>
        ) : (
          <>
            {/* Featured post */}
            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                className="block group mb-14 md:mb-20"
                data-testid={`blog-card-${featured.slug}`}
              >
                <article className="grid md:grid-cols-2 gap-6 md:gap-10 items-center rounded-md overflow-hidden border border-[#E5E0D8]" style={{ backgroundColor: '#FFFFFF' }}>
                  <div className="order-2 md:order-1 p-6 md:p-10">
                    <p
                      className="uppercase mb-3"
                      style={{ fontFamily: 'Manrope', fontSize: '11px', fontWeight: 600, letterSpacing: '0.14em', color: '#D95A41' }}
                    >
                      Featured
                    </p>
                    <h2
                      style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
                      className="text-3xl md:text-4xl leading-tight mb-4 group-hover:underline"
                    >
                      {featured.title}
                    </h2>
                    <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }} className="mb-6 line-clamp-3">
                      {featured.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-sm" style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>
                      <span className="flex items-center gap-1.5"><Calendar size={14} strokeWidth={1.5} />{formatDate(featured.created_at)}</span>
                      <span className="inline-flex items-center gap-1.5" style={{ color: '#D95A41', fontWeight: 600 }}>
                        Read the guide <ArrowRight size={14} strokeWidth={2} />
                      </span>
                    </div>
                  </div>
                  <div
                    className="order-1 md:order-2 aspect-[4/3] md:aspect-auto md:h-full flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, #F2E4C9 0%, #FCE7DE 100%)'
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'Cormorant Garamond',
                        fontSize: '92px',
                        fontWeight: 600,
                        color: '#D95A41',
                        opacity: 0.55
                      }}
                    >
                      01
                    </span>
                  </div>
                </article>
              </Link>
            )}

            {/* Rest of posts */}
            {rest.length > 0 && (
              <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                {rest.map((p, idx) => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.slug}`}
                    className="block group"
                    data-testid={`blog-card-${p.slug}`}
                  >
                    <article className="h-full rounded-md overflow-hidden border border-[#E5E0D8] flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
                      <div
                        className="aspect-[16/9] flex items-center justify-center"
                        style={{
                          background: idx % 2 === 0
                            ? 'linear-gradient(135deg, #E5EFE4 0%, #F2E4C9 100%)'
                            : 'linear-gradient(135deg, #FCE7DE 0%, #E5EFE4 100%)'
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'Cormorant Garamond',
                            fontSize: '76px',
                            fontWeight: 600,
                            color: idx % 2 === 0 ? '#2E5C41' : '#8A2E17',
                            opacity: 0.55
                          }}
                        >
                          {String(idx + 2).padStart(2, '0')}
                        </span>
                      </div>
                      <div className="p-6 flex-1 flex flex-col">
                        <h3
                          style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
                          className="text-2xl leading-tight mb-3 group-hover:underline"
                        >
                          {p.title}
                        </h3>
                        <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }} className="line-clamp-3 mb-4 flex-1">
                          {p.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-sm" style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>
                          <span className="flex items-center gap-1.5"><Calendar size={14} strokeWidth={1.5} />{formatDate(p.created_at)}</span>
                          <span className="inline-flex items-center gap-1.5" style={{ color: '#D95A41', fontWeight: 600 }}>
                            Read <ArrowRight size={14} strokeWidth={2} />
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <FaqSection heading="Questions before you dive in?" subheading="The most common things buyers and cooks ask us." />
    </div>
  );
};

export default BlogListPage;
